import { useNavigate } from '@tanstack/react-router'
import { CheckCircle2Icon, XIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	useAnchorsAlertsAnnotationsQuery,
	useAnchorsAlertsQuery,
} from '@/entities/anchor'

import AnchoringAlerts from '@/shared/components/AnchoringAlerts'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import FatalError from '@/shared/components/FatalError'
import { Operators } from '@/shared/components/query-builder'
import { Button } from '@/shared/components/ui/button'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { Content, Header, Layout } from '@/shared/components/ui/layout'
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from '@/shared/components/ui/sheet'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { ampli } from '@/shared/lib/ampli'
import { getFiltersQueryString } from '@/shared/tools/location'

import { getAnchorAssortmentAlertsTree } from '../lib/helpers'

export function AnchorAssortmentAlertsDrawer({
	isOpen,
	close,
}: {
	isOpen: boolean
	close: () => void
}) {
	const { t } = useTranslation()

	return (
		<Sheet
			modal={false}
			defaultOpen={isOpen}
			open={isOpen}
			onOpenChange={(open) => !open && close()}
		>
			<SheetContent
				onPointerDownOutside={(e) => e.preventDefault()}
				onInteractOutside={(e) => e.preventDefault()}
				disbleOverlay
				className='inset-y-2 right-2 h-auto w-[600px] gap-0 rounded-3xl border bg-background/80 backdrop-blur-xl sm:max-w-[600px]'
			>
				<ErrorBoundary>
					<SheetHeader>
						<SheetTitle className='text-base'>
							{t('anchors.alerts.drawer.title')}
						</SheetTitle>
					</SheetHeader>
					<AnchorAssortmentAlertsView close={close} />
				</ErrorBoundary>
			</SheetContent>
		</Sheet>
	)
}

export function AnchorAssortmentAlertsView({ close }: { close(): void }) {
	const { t } = useTranslation()

	const navigate = useNavigate()
	const { data: annotations, isLoading: isAnnotationsLoading } =
		useAnchorsAlertsAnnotationsQuery({})
	const {
		data: alerts,
		isLoading: isAlertsLoading,
		isError,
	} = useAnchorsAlertsQuery({})
	const isLoading = isAnnotationsLoading || isAlertsLoading
	if (isLoading) {
		return <AlertsListSkeleton close={close} />
	}
	if (isError) {
		return <FatalError />
	}
	const alertsTree = getAnchorAssortmentAlertsTree(alerts || [], annotations)
	const handleNavigate = (ids: string[]) => {
		close()
		navigate({
			to: `/p/anchor-management/assortment${getFiltersQueryString([
				{ name: 'alerts', operation: Operators.ANY_OF, value: ids },
			])}`,
		})
	}

	return (
		<Content className='overflow-y-auto'>
			<div className='w-full px-4'>
				{!alertsTree?.length ? (
					<Empty>
						<EmptyHeader>
							<EmptyMedia variant='icon'>
								<CheckCircle2Icon />
							</EmptyMedia>
							<EmptyTitle>
								{t('general_everything_is_ok', {
									defaultValue: 'Everything is ok',
								})}
							</EmptyTitle>
							<EmptyDescription>
								{t('anchors.alerts.drawer.empty_description', {
									defaultValue:
										'Everything is ok. You have no anchoring alerts.',
								})}
							</EmptyDescription>
						</EmptyHeader>
					</Empty>
				) : (
					<AnchoringAlerts
						alertsTree={alertsTree}
						onNavigate={handleNavigate}
						onAnalyticsShowNotification={(kind) =>
							ampli.alertsSectionToggled({
								alert_kind: kind,
								alerts_of: 'anchoring',
							})
						}
					/>
				)}
			</div>
		</Content>
	)
}
const AlertsListSkeleton = ({ close }: { close(): void }) => (
	<Layout className='h-full overflow-y-hidden'>
		<Header className='border-base flex items-center justify-between border-b border-solid p-5'>
			<div>
				<Skeleton className='h-6 w-24 rounded-lg' />
				<Skeleton className='mt-2 h-4 w-72 rounded-lg' />
			</div>
			<Button variant='ghost' size='icon' onClick={() => close()}>
				<XIcon />
			</Button>
		</Header>
		<div className='flex w-full flex-col justify-center p-5'>
			<Skeleton className='w-full rounded-lg bg-secondary'>
				<div className='h-12 w-full rounded-t-lg bg-accent' />
				<div className='h-12 w-full' />
			</Skeleton>
			<Skeleton className='mt-5 w-full rounded-lg bg-secondary'>
				<div className='h-12 w-full rounded-t-lg bg-accent' />
				<div className='h-12 w-full' />
			</Skeleton>
			<Skeleton className='mt-5 w-full rounded-lg bg-secondary'>
				<div className='h-12 w-full rounded-t-lg bg-accent' />
				<div className='h-12 w-full' />
			</Skeleton>
		</div>
	</Layout>
)
